import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import ApplicationPageLayout, {
  useApplicationPageLayoutMode,
} from "components/ApplicationShell/ApplicationPageLayout";
import { isOrgAdminRole } from "utils/roleUtils";
import {
  POSTHOG_IDENTITY_FLAGS,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";
import {
  getComputePageContentClassName,
  getComputePageLayoutClassName,
} from "../layout";
import NemoClawPromoBanner from "../NemoClawPromoBanner";
import ApplicationsPanel from "./ApplicationsPanel";
import ComputeListControls from "./ComputeListControls";
import ComputePanel from "./ComputePanel";
import type { ComputeEmptyStateKind } from "./ComputeEmptyState";
import EnvironmentListHeader from "./EnvironmentListHeader";
import EnvironmentListTabs, {
  type EnvironmentListTab,
} from "./EnvironmentListTabs";
import { useComputeListFilters } from "./hooks/useComputeListFilters";
import { useEnvironmentListRefresh } from "./hooks/useEnvironmentListRefresh";
import { useOrganizationComputeResourceList } from "./hooks/useOrganizationComputeResourceList";
import { useOrganizationHasComputeResources } from "./hooks/useOrganizationHasComputeResources";
import { useOrganizationTeamMembers } from "./hooks/useOrganizationTeamMembers";
import Pagination from "components/Pagination";
import { usePagination } from "components/Pagination/usePagination";
import type { ComputeListFilterUpdate } from "./hooks/useComputeListFilters";

const COMPUTE_RESOURCES_PER_PAGE = 20;

const EnvironmentListRedesign: React.FC = () => {
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const history = useHistory();
  const isApplicationShellRedesign = useApplicationPageLayoutMode() === "fluid";
  const applicationsTabEnabled =
    useIdentityFeatureFlagEnabled(
      POSTHOG_IDENTITY_FLAGS.ENVIRONMENT_LIST_APPLICATIONS_TAB,
    ) === true;
  const [selectedTab, setSelectedTab] = useState<EnvironmentListTab>("compute");
  const [search, setSearch] = useState("");
  const activeTab = applicationsTabEnabled ? selectedTab : "compute";
  const computeFiltersEnabled = activeTab === "compute";
  const { filters, setFilters } = useComputeListFilters();
  const { loadTeamMembers, teamMembers } = useOrganizationTeamMembers(
    orgContext.activeOrgId,
  );

  const activeCreatorId = computeFiltersEnabled
    ? filters.creatorId !== "all"
      ? filters.creatorId
      : filters.ownership === "mine"
        ? (userContext.me?.id ?? "__current_user_unavailable__")
        : "all"
    : "all";
  const {
    cloudEnvironments,
    computeResources,
    hasCurrentQueryResources,
    isError: hasComputeLoadError,
    isLocalConnectivityLoading,
    isLoading: isComputeLoading,
    isPageRequestPending,
    refetch: refetchComputeResources,
  } = useOrganizationComputeResourceList({
    activity: computeFiltersEnabled ? filters.status : "all",
    computeType: computeFiltersEnabled ? filters.type : "cloud",
    createdByUserId: activeCreatorId === "all" ? undefined : activeCreatorId,
    network: computeFiltersEnabled ? filters.network : "all",
    organizationId: orgContext.activeOrgId,
    search: computeFiltersEnabled ? search : "",
  });
  const { hasComputeResources, hasLoadedInitialPage } =
    useOrganizationHasComputeResources({
      organizationId: orgContext.activeOrgId,
      currentQueryHasResources: hasCurrentQueryResources,
      isCurrentQueryUnfiltered:
        computeFiltersEnabled &&
        !search.trim() &&
        filters.type === "all" &&
        filters.status === "all" &&
        filters.creatorId === "all" &&
        filters.ownership === "all" &&
        filters.network === "all",
      isInitialPageLoading: isPageRequestPending,
    });
  const { isRefreshing, refresh } = useEnvironmentListRefresh({
    loadTeamMembers,
    refetchComputeResources,
  });
  const {
    currentPage,
    paginatedItems: paginatedComputeResources,
    resetPage,
    setCurrentPage,
    totalPages,
  } = usePagination(computeResources, COMPUTE_RESOURCES_PER_PAGE);
  const updateFilters = useCallback(
    (update: ComputeListFilterUpdate) => {
      resetPage();
      setFilters(update);
    },
    [resetPage, setFilters],
  );
  const updateSearch = useCallback(
    (value: string) => {
      resetPage();
      setSearch(value);
    },
    [resetPage],
  );

  useEffect(() => resetPage(), [orgContext.activeOrgId, resetPage]);

  const creatorFilterLabel =
    filters.creatorId === "all"
      ? undefined
      : filters.creatorId === userContext.me?.id
        ? "Me"
        : (teamMembers.find((member) => member.id === filters.creatorId)
            ?.name ?? "Unknown creator");
  const selectedComputeLabel =
    filters.type === "all" ? "compute" : `${filters.type} compute`;
  const hasNonTypeComputeFilters =
    search.trim().length > 0 ||
    filters.status !== "all" ||
    filters.creatorId !== "all" ||
    filters.ownership !== "all" ||
    filters.network !== "all";
  const emptyComputeKind: ComputeEmptyStateKind = hasNonTypeComputeFilters
    ? "filtered"
    : filters.type === "local"
      ? "local"
      : filters.type === "cloud"
        ? "cloud"
        : "organization";
  const isAdmin = isOrgAdminRole(orgContext.activeOrgRole);
  const showComputeControls = hasComputeResources && hasLoadedInitialPage;
  const addCloudCompute = () => history.push("/environment/new");

  return (
    <ApplicationPageLayout
      className={getComputePageLayoutClassName({
        isApplicationShellRedesign,
        legacyClassName: "py-10 sm:px-6 lg:px-8",
      })}
    >
      <div
        className={getComputePageContentClassName({
          isApplicationShellRedesign,
          legacyClassName: "mx-auto px-4",
        })}
      >
        <EnvironmentListHeader isAdmin={isAdmin} onAddCloud={addCloudCompute} />
        <NemoClawPromoBanner />
        {applicationsTabEnabled ? (
          <EnvironmentListTabs
            activeTab={activeTab}
            onChange={setSelectedTab}
          />
        ) : null}
        <ComputeListControls
          activeTab={activeTab}
          creatorLabel={creatorFilterLabel}
          currentUserId={userContext.me?.id}
          filters={filters}
          isRefreshing={isRefreshing}
          members={teamMembers}
          onRefresh={() => void refresh()}
          search={search}
          setFilters={updateFilters}
          setSearch={updateSearch}
          show={showComputeControls}
        />
        {activeTab === "compute" ? (
          <>
            <ComputePanel
              emptyStateKind={emptyComputeKind}
              isAdmin={isAdmin}
              isConnectivityLoading={isLocalConnectivityLoading}
              isError={hasComputeLoadError}
              isLoading={isComputeLoading}
              onAddCloud={addCloudCompute}
              onRetry={() => void refetchComputeResources()}
              resources={paginatedComputeResources}
              selectedComputeLabel={selectedComputeLabel}
            />
            {!isComputeLoading ? (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            ) : null}
          </>
        ) : (
          <ApplicationsPanel
            activeOrgId={orgContext.activeOrgId}
            environments={cloudEnvironments}
            isError={hasComputeLoadError}
            isLoading={isComputeLoading}
            onOpenEnvironment={history.push}
            onRetry={() => void refetchComputeResources()}
            search={search}
          />
        )}
      </div>
    </ApplicationPageLayout>
  );
};

export default EnvironmentListRedesign;
